import { HTTP_CODES } from './HTTPCodes';

type ErrorMap = Partial<Record<HTTP_CODES, string> & { default: string }>;

const DEFAULT_MESSAGE = 'Something went wrong!';

/**
 * Response Error based on given error and a dictionary containing
 * messages with a status/code key, that will be taken if the error
 * doesn't have a message or error.
 *
 * @example
 * ```js
 * // From an error
 * const message = responseErrorMessage(new Error('Test message')) // Test message
 *
 * // From an axios response
 * const message = responseErrorMessage({ status: 200 }, { '200': 'Test message' }) // Test message
 * const message = responseErrorMessage({}, { default: 'Test message' }) // Test message
 * const message = responseErrorMessage({}, { '200': 'Test message' }) // Something went wrong!
 * ```
 * @param error
 * @param errorMap
 */
export const responseErrorMessage = (error: any, errorMap?: ErrorMap): string => {
    let message = DEFAULT_MESSAGE;
    const response = error?.response ?? error ?? {};
    const { data } = response?.data ? response : { data: response };
    const code: any = response?.status ?? error?.code ?? data?.error?.code ?? 0;

    if (data?.message) {
        message = data.message;
    } else if (data?.error) {
        message = data.error?.message || data.error;
    }

    if (/^</.test(message.trim())) {
        message = null;
    }

    if (errorMap) {
        return addLastChar((errorMap as Record<string, any>)[code] ?? errorMap.default ?? message ?? DEFAULT_MESSAGE);
    }

    return addLastChar(message ?? DEFAULT_MESSAGE);
};

function addLastChar(message: string) {
    const message$ = message.trim();
    if (/[a-z0-9]$/.test(message$)) {
        return message$ + '!';
    }

    return message$;
}
